import {
  DashboardDataTransformer,
  ForecastYearLabelDataTransformer,
  KEuroToEuroValueDataTransformer,
  PercentageValueDataTransformer,
} from '../../../data_transformers/dashboard_data_transformer';
import { Analysis, Granularity, Metric } from '../../../dashboard-config/dashboard-config.model';
import { KpiColumnContext, KpiMeasure } from '../../../dashboard-config/kpis/kpi-columns';
import {
  KpiColumnDataTransformer,
  KpiValueDisplayDataTransformer,
} from '../../../dashboard-config/kpis/kpi-data-transformers';
import { MetricProfile } from '../../../dashboard-config/metrics/metric-profile';
import type { RawCell } from '../../../export/export-controller/dashboard-export-controller';
import {
  companiesCountProfile,
  companiesRevenueProfile,
  economicParticipantsProfile,
  employedProfile,
} from '../../../dashboard-config/metrics/metric-profile-implementations';

/** Methodologies forecasts are computed for. */
export const FORECAST_METHODOLOGY_IDS = ['1', '7', '8', '9'] as const;

/** Export footnote under forecast tables. */
export const FORECAST_YEAR_NOTE: RawCell[][] = [
  [
    {
      value: '[br]',
      rowSpan: 1,
      cn: 1,
      colSpan: 1,
    },
  ],
  [
    {
      value: '** Goldmedia Prognose',
      rowSpan: 1,
      cn: 1,
      colSpan: 2,
    },
  ],
];

export interface ForecastMetricKeys {
  valueKey: string;
  valueChangeKey: string;
  valueUpperKey: string;
  valueLowerKey: string;
}

export const FORECAST_STANDARD_KEYS: ForecastMetricKeys = {
  valueKey: 'value',
  valueChangeKey: 'value_change_prev_year_pct',
  valueUpperKey: 'value_confidence_interval_high',
  valueLowerKey: 'value_confidence_interval_low',
};

/** Economic participants write into core-market keys, then alias `value` (legacy). */
export const FORECAST_ECONOMIC_PARTICIPANT_KEYS: ForecastMetricKeys = {
  valueKey: 'economic_participants_core_market',
  valueChangeKey: 'economic_participants_core_market_change_prev_year_pct',
  valueUpperKey: 'economic_participants_core_market_confidence_interval_high',
  valueLowerKey: 'economic_participants_core_market_confidence_interval_low',
};

const FORECAST_CONTEXT: KpiColumnContext = { analysis: Analysis.Forecast };

/** Selected KPI's measures into the metric's result keys. */
function kpiColumnTransformers(
  measures: [KpiMeasure, string][],
  context: KpiColumnContext,
): DashboardDataTransformer[] {
  return measures.map(
    ([measure, resultColumn]) => new KpiColumnDataTransformer({ measure, resultColumn, context }),
  );
}

/** Value, change and confidence interval of the selected KPI (forecast comparison). */
export function createForecastValueChangeCiTransformers(
  keys: ForecastMetricKeys,
  granularity?: Granularity,
): DashboardDataTransformer[] {
  return kpiColumnTransformers(
    [
      ['value', keys.valueKey],
      ['changePrevYearPct', keys.valueChangeKey],
      ['ciHigh', keys.valueUpperKey],
      ['ciLow', keys.valueLowerKey],
    ],
    { ...FORECAST_CONTEXT, granularity },
  );
}

/** Value and change of the selected KPI (forecast structure reads no CI). */
export function createForecastValueChangeTransformers(
  keys: ForecastMetricKeys,
  granularity?: Granularity,
): DashboardDataTransformer[] {
  return kpiColumnTransformers(
    [
      ['value', keys.valueKey],
      ['changePrevYearPct', keys.valueChangeKey],
    ],
    { ...FORECAST_CONTEXT, granularity },
  );
}

export function createForecastDefaultExportTransformers(
  valueChangeKey: string,
): DashboardDataTransformer[] {
  return [
    new PercentageValueDataTransformer(valueChangeKey),
    new ForecastYearLabelDataTransformer(),
  ];
}

/** Legacy export: € for the companies value; the selected KPI's change as a percentage. */
export function createForecastRevenueExportTransformers(): DashboardDataTransformer[] {
  return [
    new KEuroToEuroValueDataTransformer('companies_revenue__k_eur'),
    new KpiColumnDataTransformer({
      measure: 'changePrevYearPct',
      resultColumn: 'companies_revenue_change_prev_year_pct',
      context: FORECAST_CONTEXT,
    }),
    new PercentageValueDataTransformer('companies_revenue_change_prev_year_pct'),
    new ForecastYearLabelDataTransformer(),
  ];
}

/** `ANZAHL KERNERWERBSTÄTIGER`: the selected count KPI, article-less genitive. */
function forecastCountTitle(base: MetricProfile): (filterValues: Map<string, any>) => string {
  return (filterValues) => `ANZAHL ${base.formatKpiGenitiveLabel(filterValues)}`.toUpperCase();
}

export function forecastTitleMetricUmsaetze(filterValues: Map<string, any>): string {
  return `${companiesRevenueProfile.labels.headingPhrase} ${companiesRevenueProfile.formatKpiHeadingLabel(filterValues)}`;
}



// --- Comparison shell product profiles ---

/**
 * Forecast comparison facts of a metric. KPI selection and columns come from
 * `base` and the generic KPI pipeline.
 */
export interface ForecastComparisonMetricProfile {
  metricId: Metric;
  base: MetricProfile;
  keys: ForecastMetricKeys;
  /** The chart reads `value`; set when `keys.valueKey` is not `value`. */
  aliasChartValue?: boolean;
  titleMetric: (filterValues: Map<string, any>) => string;
  mapHeadingMetricLabel: (filterValues: Map<string, any>) => string;
  createExportTransformers: () => DashboardDataTransformer[];
}

/** Chart `value` alias and its display. */
export function createForecastChartTransformers(
  metric: ForecastComparisonMetricProfile,
  granularity?: Granularity,
): DashboardDataTransformer[] {
  return [
    ...(metric.aliasChartValue
      ? kpiColumnTransformers([['value', 'value']], { ...FORECAST_CONTEXT, granularity })
      : []),
    new KpiValueDisplayDataTransformer({ valueColumnKey: 'value', resultColumnKey: 'value_display' }),
  ];
}

export const forecastCompaniesCountProfile: ForecastComparisonMetricProfile = {
  metricId: Metric.CompaniesCount,
  base: companiesCountProfile,
  keys: FORECAST_STANDARD_KEYS,
  titleMetric: forecastCountTitle(companiesCountProfile),
  mapHeadingMetricLabel: (fv) => `PROGNOSE DER ${companiesCountProfile.formatKpiHeadingLabel(fv)}`,
  createExportTransformers: () =>
    createForecastDefaultExportTransformers(FORECAST_STANDARD_KEYS.valueChangeKey),
};

export const forecastCompaniesRevenueProfile: ForecastComparisonMetricProfile = {
  metricId: Metric.CompaniesRevenue,
  base: companiesRevenueProfile,
  keys: FORECAST_STANDARD_KEYS,
  titleMetric: forecastTitleMetricUmsaetze,
  mapHeadingMetricLabel: (fv) => `UMSATZPROGNOSE DER ${companiesRevenueProfile.formatKpiHeadingLabel(fv)}`,
  createExportTransformers: createForecastRevenueExportTransformers,
};

export const forecastEconomicParticipantProfile: ForecastComparisonMetricProfile = {
  metricId: Metric.EconomicParticipant,
  base: economicParticipantsProfile,
  keys: FORECAST_ECONOMIC_PARTICIPANT_KEYS,
  aliasChartValue: true,
  titleMetric: forecastCountTitle(economicParticipantsProfile),
  mapHeadingMetricLabel: (fv) => `PROGNOSE DER ${economicParticipantsProfile.formatKpiHeadingLabel(fv)}`,
  createExportTransformers: () =>
    createForecastDefaultExportTransformers(FORECAST_ECONOMIC_PARTICIPANT_KEYS.valueChangeKey),
};

export const forecastEmployedProfile: ForecastComparisonMetricProfile = {
  metricId: Metric.Employed,
  base: employedProfile,
  keys: FORECAST_STANDARD_KEYS,
  titleMetric: forecastCountTitle(employedProfile),
  mapHeadingMetricLabel: (fv) => `PROGNOSE DER ${employedProfile.formatKpiHeadingLabel(fv)}`,
  createExportTransformers: () =>
    createForecastDefaultExportTransformers(FORECAST_STANDARD_KEYS.valueChangeKey),
};

// --- Structure shell product profiles ---

/**
 * Forecast structure facts of a metric. KPI selection and columns come from
 * `base` and the generic KPI pipeline.
 */
export interface ForecastStructureMetricProfile {
  metricId: Metric;
  base: MetricProfile;
  keys: ForecastMetricKeys;
  titleMetric: (filterValues: Map<string, any>) => string;
  createExportTransformers: () => DashboardDataTransformer[];
}

export const forecastStructureCompaniesCountProfile: ForecastStructureMetricProfile = {
  metricId: Metric.CompaniesCount,
  base: companiesCountProfile,
  keys: FORECAST_STANDARD_KEYS,
  titleMetric: forecastCountTitle(companiesCountProfile),
  createExportTransformers: () =>
    createForecastDefaultExportTransformers(FORECAST_STANDARD_KEYS.valueChangeKey),
};

export const forecastStructureCompaniesRevenueProfile: ForecastStructureMetricProfile = {
  metricId: Metric.CompaniesRevenue,
  base: companiesRevenueProfile,
  keys: FORECAST_STANDARD_KEYS,
  titleMetric: forecastTitleMetricUmsaetze,
  createExportTransformers: createForecastRevenueExportTransformers,
};

export const forecastStructureEconomicParticipantProfile: ForecastStructureMetricProfile = {
  metricId: Metric.EconomicParticipant,
  base: economicParticipantsProfile,
  keys: FORECAST_ECONOMIC_PARTICIPANT_KEYS,
  titleMetric: forecastCountTitle(economicParticipantsProfile),
  createExportTransformers: () =>
    createForecastDefaultExportTransformers(FORECAST_ECONOMIC_PARTICIPANT_KEYS.valueChangeKey),
};

export const forecastStructureEmployedProfile: ForecastStructureMetricProfile = {
  metricId: Metric.Employed,
  base: employedProfile,
  keys: FORECAST_STANDARD_KEYS,
  titleMetric: forecastCountTitle(employedProfile),
  createExportTransformers: () =>
    createForecastDefaultExportTransformers(FORECAST_STANDARD_KEYS.valueChangeKey),
};
